import { formatMoney, t } from "@hakili/i18n";
import { ArrowUpDown, Heart, Search, SlidersHorizontal, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { VehicleCardPhone, type VehicleCard } from "./carte.js";

/**
 * `round CW`, `CW.2`: **« Trouvez votre voiture », as the drawing draws it.**
 *
 * `hakili-client.jsx`'s `Recherche`, remade on the founder's reference of
 * 8 September: the search bar PINNED, the segmented counts saying what each tab
 * holds before it is opened, a sort row beside them, and the settings that do
 * not change with every gesture folded into « Filtres » with the number of
 * active ones.
 *
 * ## What is derived here and what is asked of a door
 *
 * The list, its filters and the monthly payment are the SEARCH door's
 * (`GET /api/v1/vehicles`). The two lists are their own doors. The sort is done
 * here, over what the door returned, because it is a reordering of one answer
 * and not a different question , the drawing sorts the same rows four ways.
 *
 * **The counts are lengths of what the doors answered**, never constants: `R81`
 * forbids a number nobody computed.
 *
 * ## The counts are of the whole park, before the radius
 *
 * The drawing says so in its own words, and the reason is that
 * « Mes favourites · 0 » would LIE to a buyer who has simply narrowed his radius.
 * So the tabs count what each list holds; only the visible rows are
 * filtered.
 */

export interface SearchProps {
  readonly vehicles: readonly VehicleCard[];
  readonly favourites: readonly string[];
  readonly passedList: readonly string[];
  /**
   * `round C2`, ruling 4: **the same question without the RADIUS.**
   *
   * These rows are what the door answers for the ceiling, the make, the city
   * and the free text the buyer has set, over the whole country. Two things are
   * read off them and both used to be read off something else:
   *
   * 1. the segment COUNTS , what each segment would show, which is the ruling;
   * 2. « Voir N véhicule(s) plus loin dans le pays », which was a number the
   *    shell computed and handed over. Derived here now, from the two lists it
   *    is the difference between, so the count and the rows cannot drift.
   */
  readonly everywhere: readonly VehicleCard[];
  readonly cities: readonly string[];
  readonly makes: readonly string[];
  readonly city: string;
  readonly discoverOpen: boolean;
  readonly onCity: (city: string) => void;
  readonly onRadius: (km: number) => void;
  readonly onBudget: (montant: number) => void;
  readonly onMake: (make: string) => void;
  readonly onText: (text: string) => void;
  readonly radius: number;
  readonly budget: number;
  readonly make: string;
  /** `C2.5`: the order the buyer chose. It is a QUERY, so the shell holds it. */
  readonly tri: string;
  readonly onTri: (tri: string) => void;
  readonly text: string;
  readonly onKeep: (id: string) => void;
  readonly onPass: (id: string) => void;
  readonly onClearPassed: () => void;
  readonly onOpen: (id: string) => void;
}

/** The drawing's radius chips, in its own order. */
export const RADII = [
  { km: 50, key: "client.recherche.rayonVille" },
  { km: 300, key: "client.recherche.rayon300" },
  { km: 9999, key: "client.recherche.rayonMali" },
] as const;

/** The drawing's four sorts, `TRIS_CLIENT`, in its own order. */
export const SORTS = [
  { id: "mensualite", key: "client.recherche.triMensualite" },
  { id: "prix", key: "client.recherche.triPrix" },
  { id: "proches", key: "client.recherche.triProches" },
  { id: "km", key: "client.recherche.triKm" },
] as const;
export type Tri = (typeof SORTS)[number]["id"];
/**
 * The slider's top notch , the position that hides nothing (ruling 5).
 *
 * Exported because the SHELL must know it: at this value it sends no
 * `maxMensualite` at all, and a second copy of the number in `App` is how the
 * label and the query would come to disagree about what « Sans limite » means.
 */
export const PLAFOND_SANS_LIMITE = 300_000;

/**
 * « Tout le Mali », the radius that hides nothing , the door's own
 * `RADIUS_CHIPS.toutLeMali`. Named here because `activeSettings` asks whether a
 * radius NARROWS, and a literal `9999` in that question is a number a reader
 * has to recognise.
 */
export const RADIUS_TOUT_LE_MALI = 9999;

export type Segment = "tous" | "favourites" | "passedList";

/**
 * The sort, over the rows the door answered.
 *
 * **An absent value sorts LAST in every order**, and never as zero: a car whose
 * distance nobody can compute is not the nearest one, and a car with no monthly
 * payment is not the cheapest. That is the same rule the card draws as a dash.
 */
export function sortRows(rows: readonly VehicleCard[], tri: Tri): readonly VehicleCard[] {
  const dernier = Number.POSITIVE_INFINITY;
  const key = (row: VehicleCard): number => {
    if (tri === "prix") return row.priceAmount;
    if (tri === "proches") return row.distanceKm ?? dernier;
    if (tri === "km") return row.mileageKm ?? dernier;
    return row.monthlyAmount ?? dernier;
  };
  return [...rows].sort((a, b) => key(a) - key(b));
}

/** How many of the folded settings the buyer has actually changed. */
/**
 * `round C3`, `C3.0(c)` and `(e)`: **it counts what NARROWS, never what is set.**
 *
 * It counted a difference from a default: a city other than the first in the
 * list, a radius other than « Tout le Mali », a budget other than 150 000. Two
 * of those three are not narrowings at all.
 *
 * - **The CITY is the buyer's position** (`(e)`), not a filter on the car. He
 *   is somewhere; being somewhere hides nothing. It decides the origin the
 *   distance is measured from and the radius is measured against, and a badge
 *   counting it told a buyer standing in Ségou that he had set a filter he had
 *   never chosen.
 * - **The CEILING at « Sans limite »** hides nothing, and it is the default
 *   since `(c)` , counting a difference from 150 000 would have made the very
 *   first screen read « Filtres · 1 ».
 *
 * So each term asks the same question: does this setting REMOVE a car from the
 * answer? A radius under « Tout le Mali » does. A make does. A ceiling under
 * « Sans limite » does. A position does not.
 */
export function activeSettings(input: {
  readonly radius: number;
  readonly budget: number;
  readonly make: string;
}): number {
  return (
    (input.radius < RADIUS_TOUT_LE_MALI ? 1 : 0) +
    (input.budget < PLAFOND_SANS_LIMITE ? 1 : 0) +
    (input.make === "" ? 0 : 1)
  );
}

function Chip({
  actif,
  onClick,
  testid,
  children,
}: {
  readonly actif: boolean;
  readonly onClick: () => void;
  readonly testid: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={actif}
      onClick={onClick}
      className={`shrink-0 rounded-xl border px-3 py-2 text-xs font-medium tabular-nums ${
        actif ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-600"
      }`}
    >
      {children}
    </button>
  );
}

export function Recherche(props: SearchProps): React.JSX.Element {
  const [filtersOpen, setFiltresOuverts] = useState(false);
  const [segment, setSegment] = useState<Segment>("tous");
  const [mode, setMode] = useState<"liste" | "decouvrir">("liste");

  // `Découvrir` is a mode and not a tab; if the api closes it mid-session the
  // buyer must not be left standing in a screen that no longer exists.
  useEffect(() => {
    if (!props.discoverOpen) setMode("liste");
  }, [props.discoverOpen]);

  const inSegment = (row: VehicleCard): boolean =>
    segment === "favourites"
      ? props.favourites.includes(row.id)
      : segment === "passedList"
        ? props.passedList.includes(row.id)
        : true;

  // Written out rather than memoised behind a disable: `inSegment` closes over
  // the two lists and the segment, and a lint rule this config does not carry
  // cannot be silenced by a comment pretending it does.
  /**
   * `round C2`, `C2.5`: **the order is the DOOR's, and this screen renders it.**
   *
   * `sortRows` used to run here, over the page the door had answered. That is
   * an order about the page and not about the catalogue, and the founder walked
   * it and said the sorts were « not really working ». The chosen sort travels
   * as `tri` now and the rows arrive in sequence; filtering by segment PRESERVES
   * that sequence, because `filter` does.
   */
  const visible = props.vehicles.filter(inSegment);

  const stack = useMemo(
    () =>
      props.vehicles.filter(
        (row) => !props.favourites.includes(row.id) && !props.passedList.includes(row.id),
      ),
    [props.vehicles, props.favourites, props.passedList],
  );

  const activeCount = activeSettings({
    radius: props.radius,
    budget: props.budget,
    make: props.make,
  });

  /**
   * `round C2`, ruling 4: **a segment counts what it would SHOW.**
   *
   * The drawing's rule was that the counts are of the whole park , *« sinon
   * « Mes favoris · 0 » mentirait à qui a réduit son rayon »* , and it was
   * right about the radius for a reason that is a CONTROL: a buyer who has
   * narrowed his radius has « Voir N véhicule(s) plus loin dans le pays » to
   * widen it again.
   *
   * The founder's decision of 9 September turned the monthly ceiling into a
   * filter, and the same count then lied the other way: a car kept above the
   * ceiling was counted and not shown , « Mes favoris · 1 » over an empty list,
   * with nothing on the screen saying a car was being withheld. So the ceiling
   * and the make count, and the radius does not, which is what
   * {@link SearchProps.everywhere} is for.
   */
  const countIn = (segmentId: Segment): number =>
    segmentId === "favourites"
      ? props.everywhere.filter((row) => props.favourites.includes(row.id)).length
      : segmentId === "passedList"
        ? props.everywhere.filter((row) => props.passedList.includes(row.id)).length
        : props.everywhere.length;

  const tabs: readonly { id: Segment; key: string; count: number }[] = [
    { id: "tous", key: "client.recherche.segTous", count: countIn("tous") },
    { id: "favourites", key: "client.recherche.segFavoris", count: countIn("favourites") },
    { id: "passedList", key: "client.recherche.segPasses", count: countIn("passedList") },
  ];

  /** What the RADIUS hides, and nothing else: the difference of two answers. */
  const beyondRadius = Math.max(0, props.everywhere.length - props.vehicles.length);

  const inDiscover = mode === "decouvrir" && props.discoverOpen;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.recherche.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {inDiscover ? t("client.recherche.sousDecouvrir") : t("client.recherche.sousListe")}
        </p>
      </div>

      {/* Pinned: it does not leave with the scroll. */}
      <div className="sticky top-0 z-30 -mx-4 bg-gray-50 px-4 pb-2 pt-1">
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center gap-2.5 rounded-2xl border border-gray-200 bg-white px-3.5 py-3">
            <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
            <input
              data-testid="recherche-text"
              value={props.text}
              onChange={(event) => {
                props.onText(event.target.value);
              }}
              placeholder={t("client.recherche.placeholder")}
              className="min-w-0 flex-1 text-sm outline-none"
            />
            {props.text === "" ? null : (
              <button
                type="button"
                data-testid="recherche-effacer"
                aria-label={t("common.action.effacer")}
                onClick={() => {
                  props.onText("");
                }}
                className="shrink-0 text-gray-400"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
          <button
            type="button"
            data-testid="recherche-filtres"
            aria-expanded={filtersOpen}
            onClick={() => {
              setFiltresOuverts(!filtersOpen);
            }}
            className={`flex shrink-0 items-center gap-1.5 rounded-2xl border px-3 py-3 ${
              filtersOpen || activeCount > 0
                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white text-gray-600"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs font-medium">
              {t("client.recherche.filtres")}
              {activeCount > 0 ? ` · ${String(activeCount)}` : ""}
            </span>
          </button>
        </div>
      </div>

      {filtersOpen ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5" data-testid="recherche-fold">
          <div className="mb-2.5 flex items-center justify-between">
            <span className="text-sm text-gray-600">{t("client.recherche.vousEtesA")}</span>
            <select
              data-testid="recherche-city"
              value={props.city}
              onChange={(event) => {
                props.onCity(event.target.value);
              }}
              className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium outline-none"
            >
              {props.cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>

          {/* The founder's decision of 8 September: « Marque », from the same
              reference the dealer's own form reads. It is drawn in WORDS in the
              client addendum and its pair waits for the designer's drawing. */}
          <div className="mb-2.5 flex items-center justify-between">
            <span className="text-sm text-gray-600">{t("client.recherche.marque")}</span>
            <select
              data-testid="recherche-make"
              value={props.make}
              onChange={(event) => {
                props.onMake(event.target.value);
              }}
              className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium outline-none"
            >
              <option value="">{t("client.recherche.toutesMarques")}</option>
              {props.makes.map((make) => (
                <option key={make} value={make}>
                  {make}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3.5 grid grid-cols-3 gap-2">
            {RADII.map((chip) => (
              <Chip
                key={chip.km}
                testid={`recherche-radius-${String(chip.km)}`}
                actif={props.radius === chip.km}
                onClick={() => {
                  props.onRadius(chip.km);
                }}
              >
                {t(chip.key)}
              </Chip>
            ))}
          </div>

          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-gray-600">{t("client.recherche.mensualiteMax")}</span>
            {/**
             * `round C2`, ruling 5: **the top notch is the ABSENCE of a
             * ceiling, and it says so.**
             *
             * The range stops at 300 000 FCFA a month and the ceiling FILTERS
             * since the founder's decision, so between the two a car whose
             * instalment is above 300 000 could not be shown to anybody, at any
             * setting, ever , the pair park's two dearest are 354 114 and
             * 459 037. That was never the decision: it is an accident of a
             * range drawn when the ceiling only dimmed a card. `03`
             * `WF-CLI-03` §4's range is amended by record as the consequence,
             * flagged, the founder's word to overrule at his walk.
             *
             * It is a LABEL and not a bigger number, because there is no
             * honest number: a ceiling of ten million is still a ceiling, and
             * the day a dearer car is listed it would hide it again.
             */}
            <span data-testid="recherche-budget-valeur" className="text-sm font-semibold tabular-nums">
              {props.budget >= PLAFOND_SANS_LIMITE
                ? t("client.recherche.sansLimite")
                : formatMoney(props.budget)}
            </span>
          </div>
          <input
            data-testid="recherche-budget"
            type="range"
            min="50000"
            max={String(PLAFOND_SANS_LIMITE)}
            step="10000"
            value={props.budget}
            onChange={(event) => {
              props.onBudget(Number(event.target.value));
            }}
            className="w-full"
          />
          {/**
           * `round C2`, `C2.4`: **the sentence goes with the card it
           * described.**
           *
           * It read *« Au-dessus de votre mensualité, un véhicule reste visible
           * mais s'affiche en retrait »*. Since the founder's decision of
           * 9 September the ceiling FILTERS , nothing above it is shown , so the
           * sentence would be describing a behaviour the product no longer has,
           * which is worse than saying nothing.
           */}
        </div>
      ) : null}

      {props.discoverOpen ? (
        <div className="grid grid-cols-2 gap-2 rounded-2xl border border-gray-200 bg-white p-1">
          {(
            [
              { id: "liste", key: "client.recherche.modeListe" },
              { id: "decouvrir", key: "client.recherche.modeDecouvrir" },
            ] as const
          ).map((choix) => (
            <button
              key={choix.id}
              type="button"
              data-testid={`recherche-mode-${choix.id}`}
              aria-pressed={mode === choix.id}
              onClick={() => {
                setMode(choix.id);
              }}
              className={`flex items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-medium ${
                mode === choix.id ? "bg-indigo-600 text-white" : "text-gray-600"
              }`}
            >
              {choix.id === "decouvrir" ? <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> : null}
              {t(choix.key)}
            </button>
          ))}
        </div>
      ) : null}

      {inDiscover ? (
        <Discover
          stack={stack}
          favourites={props.favourites}
          passedList={props.passedList}
          passedCount={props.passedList.length}
          onLike={props.onKeep}
          onPass={props.onPass}
          onClearPassed={props.onClearPassed}
          onOpen={props.onOpen}
        />
      ) : (
        <>
          <div>
            <div className="no-scrollbar flex items-center gap-2 overflow-x-auto pb-1">
              {tabs.map((one) => (
                <Chip
                  key={one.id}
                  testid={`recherche-segment-${one.id}`}
                  actif={segment === one.id}
                  onClick={() => {
                    setSegment(one.id);
                  }}
                >
                  {t(one.key)} · {one.count}
                </Chip>
              ))}
            </div>
            <div className="no-scrollbar mt-1.5 flex items-center gap-2 overflow-x-auto pb-1">
              <span className="flex shrink-0 items-center gap-1.5 text-xs text-gray-400">
                <ArrowUpDown className="h-3.5 w-3.5" aria-hidden="true" />
                {t("client.recherche.trier")}
              </span>
              {SORTS.map((one) => (
                <Chip
                  key={one.id}
                  testid={`recherche-tri-${one.id}`}
                  actif={props.tri === one.id}
                  onClick={() => {
                    props.onTri(one.id);
                  }}
                >
                  {t(one.key)}
                </Chip>
              ))}
            </div>
            {/* `round C3-B`, `C3-B.1`: the legend at the head of the list ,
                one sentence, one home, in the sort row's own type. Every
                « à partir de » below it is the search door's ESTIMATE. */}
            <p data-testid="recherche-legende" className="mt-1 text-xs leading-relaxed text-gray-400">
              {t("client.recherche.legendeEstimation")}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {visible.map((row) => (
              <VehicleCardPhone
                key={row.id}
                vehicule={row}
                kept={props.favourites.includes(row.id)}
                passed={props.passedList.includes(row.id)}
                overBudget={row.monthlyAmount !== null && row.monthlyAmount > props.budget}
                onKeep={() => {
                  props.onKeep(row.id);
                }}
                onOpen={() => {
                  props.onOpen(row.id);
                }}
              />
            ))}

            {segment === "passedList" && props.passedList.length > 0 ? (
              <button
                type="button"
                data-testid="recherche-revoir-passedList"
                onClick={props.onClearPassed}
                className="w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700"
              >
                {t("client.recherche.revoirPasses")}
              </button>
            ) : null}

            {/* Counts only what the RADIUS hides , never the segment and never
                the search. The drawing keeps that rule and widens it. */}
            {beyondRadius > 0 && segment === "tous" ? (
              <button
                type="button"
                data-testid="recherche-plus-far"
                onClick={() => {
                  props.onRadius(9999);
                }}
                className="w-full rounded-2xl border border-indigo-200 bg-white py-3 text-sm font-medium text-indigo-600"
              >
                {t("client.recherche.plusLoinAvant")} {beyondRadius}{" "}
                {t(beyondRadius > 1 ? "client.recherche.plusLoinApresN" : "client.recherche.plusLoinApres1")}
              </button>
            ) : null}

            {visible.length === 0 ? (
              <p
                data-testid="recherche-vide"
                className="py-8 text-center text-sm leading-relaxed text-gray-500"
              >
                {t(emptyKey(segment, props.text))}
              </p>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Which empty sentence, and the drawing has FOUR where `03` records two.
 *
 * They are not interchangeable: a buyer with no favourites is told how to make
 * one, a buyer who has passed nothing is told a pass is never a deletion, and a
 * search that matched nothing is not a radius that hid everything.
 */
export function emptyKey(segment: Segment, text: string): string {
  if (segment === "favourites") return "client.recherche.videFavoris";
  if (segment === "passedList") return "client.recherche.videPasses";
  if (text.trim() !== "") return "client.recherche.videRecherche";
  return "client.recherche.videRayon";
}

/**
 * « Découvrir » , one car at a time, the founder's `ref-09` idea behind its flag.
 *
 * The two guards the drawing declares non-negotiable are the product's, not this
 * component's: a passed car enters a list of the buyer's OWN and is never hidden
 * from the search , which is true at the door , and « Revoir les véhicules
 * passés » always exists, which is the control below.
 */
function Discover({
  stack,
  favourites,
  passedList,
  passedCount,
  onLike,
  onPass,
  onClearPassed,
  onOpen,
}: {
  readonly stack: readonly VehicleCard[];
  /** `C2.2`: the SAME two lists the counts derive from. Nothing is kept here. */
  readonly favourites: readonly string[];
  readonly passedList: readonly string[];
  readonly passedCount: number;
  readonly onLike: (id: string) => void;
  readonly onPass: (id: string) => void;
  readonly onClearPassed: () => void;
  readonly onOpen: (id: string) => void;
}): React.JSX.Element {
  const top = stack[0] ?? null;
  if (top === null) {
    return (
      <div
        data-testid="decouvrir-vide"
        className="rounded-2xl border border-gray-200 bg-white px-5 py-8 text-center"
      >
        <p className="text-sm font-semibold">{t("client.recherche.decouvrirFini")}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
          {passedCount > 0
            ? t("client.recherche.decouvrirFiniPasses")
            : t("client.recherche.decouvrirFiniRien")}
        </p>
        {passedCount > 0 ? (
          <button
            type="button"
            data-testid="decouvrir-revoir"
            onClick={onClearPassed}
            className="mt-5 w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700"
          >
            {t("client.recherche.revoirPasses")}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5" data-testid="decouvrir">
      {/**
       * `round C2`, `C2.2`: **the heart derives from the list the counts derive
       * from**, here as everywhere.
       *
       * It was the literal `false`, so Découvrir's card could never know a car
       * the buyer had already kept , « Mes favoris · 1 » moved and the heart did
       * not, which is the founder's own finding. One reading, nothing kept by
       * the card.
       */}
      <VehicleCardPhone
        vehicule={top}
        kept={favourites.includes(top.id)}
        passed={passedList.includes(top.id)}
        overBudget={false}
        // The drawing's Découvrir card carries no heart: the gesture here is
        // « J'aime », and a second way to do it on the card itself is what made
        // the count move while the card advanced under the founder's thumb.
        showKeep={false}
        onKeep={() => {
          onLike(top.id);
        }}
        onOpen={() => {
          onOpen(top.id);
        }}
      />
      {/**
       * `C2.3`: **the two commands stay UNDER THE HAND**, which is the drawing's
       * own reason: *« elles se collent au bas de l'écran tant que la carte
       * dépasse, pour qu'un pouce n'ait jamais à chercher Passer ou J'aime »*.
       * They scrolled away with the card before.
       */}
      <div className="sticky bottom-0 -mx-5 bg-gray-50 px-5 pb-1 pt-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            data-testid="decouvrir-onPass"
            onClick={() => {
              onPass(top.id);
            }}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3.5 text-sm font-medium text-gray-700"
          >
            <X className="h-4 w-4 text-gray-400" aria-hidden="true" />
            {t("client.recherche.passer")}
          </button>
          <button
            type="button"
            data-testid="decouvrir-onLike"
            onClick={() => {
              onLike(top.id);
            }}
            aria-pressed={favourites.includes(top.id)}
            /**
             * `C2.3`, `R81` as amended: on a car already kept this is the
             * drawing's TOGGLE and never a greyed control with no reason. It says
             * what it will do, and it does it.
             */
            /**
             * `C2.9`, read off the pair: **`rose-500` and `rose-200` are not
             * colours this product has.**
             *
             * `packages/config/tailwind/preset.js` REPLACES `theme.colors` with
             * 04 §4.6.3's closed palette , rose carries 50, 600 and 700 and
             * nothing else , so `bg-rose-500` compiles to no rule at all. The
             * button was drawn white on white: a control the founder cannot see
             * on a screen this round built. `no-app-colour` did not catch it
             * because the rule looks for an INVENTED colour (`bg-[#f43f5e]`, a
             * config that declares its own) and this is a known family wearing
             * a shade the preset does not carry. Booked as its own law and its
             * own guard; the other inert classes are named in the report.
             */
            className={`flex flex-1 items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-medium ${
              favourites.includes(top.id)
                ? "border border-rose-600 bg-white text-rose-600"
                : "bg-rose-600 text-white"
            }`}
          >
            {/* The drawing puts a filled heart in this button , the gesture is
                named twice, by the word and by the glyph, which is what a thumb
                aiming at one of two large controls needs. */}
            <Heart
              className={`h-4 w-4 ${favourites.includes(top.id) ? "fill-rose-600" : "fill-white"}`}
              aria-hidden="true"
            />
            {favourites.includes(top.id) ? t("client.recherche.jaimeDeja") : t("client.recherche.jaime")}
          </button>
        </div>
        {/**
         * `C2.3`: **« Voir la fiche complète », under the two**, as the drawing
         * places it. Découvrir is a thumb sorting cars; the fiche is where a
         * buyer goes when one of them stops him, and it had no way in.
         */}
        <button
          type="button"
          data-testid="decouvrir-ouvrir"
          onClick={() => {
            onOpen(top.id);
          }}
          className="mt-2 w-full rounded-2xl border border-indigo-200 bg-white py-3 text-sm font-medium text-indigo-600"
        >
          {t("client.recherche.voirFiche")}
        </button>
      </div>
      <p className="text-center text-xs leading-relaxed text-gray-400">
        {t("client.recherche.decouvrirRappel")}
      </p>
    </div>
  );
}
